import { Button } from '@src/components/Button';
import { ButtonType } from '@src/types';
import {
  type ChangeEvent,
  type HTMLAttributes,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Icon } from '../Icon';
import { IconType } from '@src/IconType';
import { classNames } from '@utils/classNames';
import { getInitials } from '@src/utils/getInitials';
// import { useIsTouchDevice } from '@src/hooks/useIsTouchDevide';
import DirectusMedia, { type DirectusMediaProps } from '../DirectusMedia';
import Image, { type ImageProps } from '@src/components/Image';

type Size = 'small' | 'medium' | 'large' | 'xlarge';

type AvatarProps = {
  variant?: 'circular' | 'square';
  className?: string;
  disabled?: boolean;
  onDelete?: () => void;
  onUpload?: (file: File) => void;
  name?: string;
  alt?: string;
  size?: Size;
  type: 'url' | 'directusFile';
  src?: string | undefined;
  asset?: string | undefined;
} & (
  | Partial<ImageProps>
  | Omit<DirectusMediaProps, 'id'>
  | HTMLAttributes<HTMLDivElement>
);
// This is the correct type, but TypeScript can't handle it 😢
//  /** URL to an image */ & (
//   | ({
//       type: 'url';
//       src: string;
//     } & Partial<ImageProps>)
//   /** Id of directus file */
//   | ({
//       type: 'directusFile';
//       asset: ID;
//     } & Omit<DirectusMediaProps, 'id'>)
//   | ({
//       src: undefined;
//       asset: undefined;
//     } & HTMLAttributes<HTMLDivElement>)
// );

export const Avatar = ({
  variant = 'square',
  className,
  disabled,
  onDelete,
  onUpload,
  name,
  type,
  src,
  asset,
  alt,
  size = 'medium',
  ...rest
}: Readonly<AvatarProps>) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = () => {
    if (!fileInputRef.current) {
      return;
    }
    fileInputRef.current.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
    }
  };

  const handleDelete = useCallback(() => {
    if (onDelete && !disabled) {
      onDelete();
    }
  }, [disabled, onDelete]);

  const initials = useMemo(() => (name ? getInitials(name) : null), [name]);

  const shapeClasses = useMemo(() => {
    if (variant === 'circular') {
      return 'rounded-full';
    } else {
      return 'rounded-md';
    }
  }, [variant]);

  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'small':
        return 'size-8';
      case 'medium':
        return 'size-10';
      case 'large':
        return 'size-18';
      case 'xlarge':
        return 'size-26';
    }
  }, [size]);

  const buttonSizeClasses = useMemo(() => {
    switch (size) {
      case 'small':
        return '!p-0.5';
      case 'medium':
        return '!p-1';
      default:
        return '!p-1.5';
    }
  }, [size]);

  const hasImage = useMemo(() => !!(src ?? asset), [src, asset]);

  const imageComponent = useMemo(() => {
    const classes = classNames(
      shapeClasses,
      sizeClasses,
      !disabled && 'group-hover:opacity-70 transition-opacity duration-300',
      'm-auto object-cover',
    );

    if (type === 'url' && src) {
      return <Image {...rest} className={classes} alt={alt} src={src}></Image>;
    } else if (type === 'directusFile' && asset) {
      return (
        <DirectusMedia
          filename_download=""
          {...rest}
          className={classes}
          id={asset}
        />
      );
    } else {
      return (
        <div
          {...rest}
          className={classNames(
            shapeClasses,
            sizeClasses,
            'text-xl bg-gray-100 font-medium text-slate-500 flex justify-center items-center',
          )}
        >
          {initials ? (
            <span
              className={classNames(
                sizeClasses,
                'flex justify-center items-center',
              )}
            >
              {initials}
            </span>
          ) : (
            <Icon type={IconType.User} className="size-8" />
          )}
        </div>
      );
    }
  }, [
    alt,
    asset,
    disabled,
    initials,
    rest,
    shapeClasses,
    sizeClasses,
    src,
    type,
  ]);

  return (
    <div className={classNames(className, 'relative group shrink-0')}>
      {imageComponent}
      <ButtonWrapper
        className={classNames(shapeClasses, sizeClasses)}
        disabled={disabled}
      >
        <Button
          buttonType={ButtonType.BLACK}
          IconLeading={hasImage ? IconType.TrashAlt : IconType.UploadSimple}
          className={classNames('!rounded-full', buttonSizeClasses)}
          onClick={hasImage ? handleDelete : handleFileUpload}
        />
        {!hasImage && (
          <input
            onChange={handleFileChange}
            ref={fileInputRef}
            className="hidden"
            type="file"
          ></input>
        )}
      </ButtonWrapper>
    </div>
  );
};

const ButtonWrapper = ({
  children,
  className,
  disabled,
}: PropsWithChildren<{
  className: string;
  disabled?: boolean;
}>) => {
  // TODO: This was not working any more - no idea why
  // const isTouchDevice = useIsTouchDevice();

  if (disabled) {
    return <></>;
  }
  return (
    <div
      className={classNames(
        className,
        'absolute flex justify-center items-center',
        'opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30',
      )}
    >
      {children}
    </div>
  );
};
